import { useEffect, useState } from "react";
import axios from "axios";
import { PageTabs } from "../component/list/PageTabs";
import { Box } from "@mui/material";
import { GetXLSXPPRModal } from "../component/GetXLSXPPRModal";
import { CONST } from "../config/const";

import TableTopBar from "../component/TableTopBar";
import TableProductPartnerResult from "../component/TableProductPartnerResult";
import { SerchSideBar } from "../component/SerchSideBar";

import { useSetAtom, useAtomValue } from "jotai";
import { AppAtoms } from "../lib/store";

import {
  getProductPartnerResult,
  ProductPartnerResultResponse,
} from "../api/productPartnerResult";
import {
  getSerchSaleCompanys,
  getSerchHighRankCompanys,
  getSerchProducts,
} from "../api/serch";
import { productPartnerResultTableColumn } from "../config/productPartnerResult";
import { getFirstYear, getOneYear } from "../lib/other";

type SaleCompany = { id: number; sale_company_name: string };
type HighRankCompany = { id: number; high_rank_company_name: string };
type Product = { id: number; product_name: string };

function getInitialMonthRange() {
  const now = new Date();
  const endYear = now.getFullYear();
  const endMonth = now.getMonth() + 1;
  let startYear = endYear;
  let startMonth = endMonth - 5;
  if (startMonth <= 0) {
    startMonth += 12;
    startYear -= 1;
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    start: `${startYear}-${pad(startMonth)}`,
    end: `${endYear}-${pad(endMonth)}`,
  };
}

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function ProductPartnerResult() {
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const setIsLoading = useSetAtom(AppAtoms.isLoading);
  const loginUser = useAtomValue(AppAtoms.loginUser);

  const [serchOpen, setSerchOpen] = useState(false);
  const [displayMode, setDisplayMode] = useState<"月別" | "日別">("月別");
  const [saleCompanys, setSaleCompanys] = useState<SaleCompany[]>([]);
  const [highRankCompanys, setHighRankCompanys] = useState<HighRankCompany[]>(
    [],
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [data, setData] = useState<ProductPartnerResultResponse | null>(null);
  const [displayNumber, setDisplayNumber] = useState(25);
  const [pageNumber, setPageNumber] = useState(1);
  const DISPLAY_NUMBERS = [10, 25, 50];

  const firstYear = getFirstYear();
  const { thisYear } = getOneYear();

  const initialRange = getInitialMonthRange();
  const serchColumnDef = {
    start_month: initialRange.start,
    end_month: initialRange.end,
    year_month: getCurrentMonth(),
    sale_company_name: "",
    high_rank_company_name: "",
    product_name: "",
  };
  const [serchString, setSerchString] = useState<{ [key: string]: string }>(
    serchColumnDef,
  );
  const [serchStringView, setSerchStringView] = useState<{
    [key: string]: string;
  }>(serchColumnDef);

  // パートナー一覧取得
  useEffect(() => {
    (async () => {
      const result = await getSerchSaleCompanys();
      if (result?.saleCompanys) {
        setSaleCompanys(result.saleCompanys as SaleCompany[]);
      }
    })();
  }, []);

  // 上位店・商材一覧取得
  useEffect(() => {
    (async () => {
      const hrcResult = await getSerchHighRankCompanys();
      if (hrcResult?.highRankCompanys) {
        setHighRankCompanys(hrcResult.highRankCompanys as HighRankCompany[]);
      }
      const productResult = await getSerchProducts();
      if (productResult?.products) {
        setProducts(productResult.products as Product[]);
      }
    })();
  }, []);

  // serchString または displayMode 変更時にAPIを呼び出す
  useEffect(() => {
    fetchResult(undefined, 1);
    setPageNumber(1);
  }, [serchString, displayMode]);

  // パラメータ構築helper: getProductPartnerResultと同一キーセット(displayNumber/pageNumber除く)
  const buildPPRSearchParams = () => {
    const partnerNames = serchString.sale_company_name
      ? serchString.sale_company_name.split(",").map((n) => n.trim()).filter(Boolean)
      : [];
    const partnerIds = saleCompanys
      .filter((c) => partnerNames.includes(c.sale_company_name))
      .map((c) => c.id);

    const params: Parameters<typeof getProductPartnerResult>[0] = {
      mode: displayMode === "月別" ? "monthly" : "daily",
    };
    if (displayMode === "月別") {
      params.start_month = serchString.start_month;
      params.end_month = serchString.end_month;
    } else {
      params.year_month = serchString.year_month;
    }
    if (partnerIds.length > 0) {
      params.partner_ids = partnerIds.join(",");
    }

    const hrcNames = serchString.high_rank_company_name
      ? serchString.high_rank_company_name.split(",").map((n) => n.trim()).filter(Boolean)
      : [];
    const hrcIds = highRankCompanys
      .filter((c) => hrcNames.includes(c.high_rank_company_name))
      .map((c) => c.id);
    if (hrcIds.length > 0) {
      params.high_rank_company_ids = hrcIds.join(",");
    }

    const productNameFilters = serchString.product_name
      ? serchString.product_name.split(",").map((n) => n.trim()).filter(Boolean)
      : [];
    const productFilterIds = products
      .filter((p) => productNameFilters.includes(p.product_name))
      .map((p) => p.id);
    if (productFilterIds.length > 0) {
      params.product_ids = productFilterIds.join(",");
    }

    return params;
  };

  const fetchResult = async (
    overrideDisplayNumber?: number,
    overridePage?: number,
  ) => {
    const dn =
      overrideDisplayNumber !== undefined
        ? overrideDisplayNumber
        : displayNumber;
    const pg = overridePage !== undefined ? overridePage : pageNumber;
    setIsLoading(true);
    const params = buildPPRSearchParams();
    params.displayNumber = String(dn);
    params.pageNumber = String(pg);
    const result = await getProductPartnerResult(params);
    setData(result);
    setIsLoading(false);
  };

  const handleSetSerchOpen = (open: boolean) => {
    setSerchOpen(open);
  };

  const handleSetDisplayMode = (mode: "日別 / 月別" | "日別" | "月別") => {
    setDisplayMode(mode as "日別" | "月別");
  };

  const getXLSXDataTypes: { auth: string[]; name: string }[] = [
    {
      auth: ["マスターアカウント", "スタッフアカウント", "パートナー", "営業アカウント"],
      name: "推進実績出力",
    },
  ];

  const [getXLSXDataNames, setGetXLSXDataNames] = useState<string[]>([]);
  const [getXLSXDataName, setGetXLSXDataName] = useState<string>("");
  const [getXLSXStatus, setGetXLSXStatus] = useState<{
    status: "none" | "open" | "getting" | "result" | "loading" | "error";
    message: string;
  }>({
    status: "none",
    message: "",
  });

  useEffect(() => {
    const names: string[] = [];
    getXLSXDataTypes.map((dataType) => {
      if (
        loginUser?.authority &&
        dataType.auth.findIndex((authority) => authority === loginUser.authority) > -1
      ) {
        names.push(dataType.name);
      }
    });
    setGetXLSXDataNames(names);
  }, [loginUser]);

  useEffect(() => {
    if (getXLSXDataName === "推進実績出力") {
      setGetXLSXStatus({ ...getXLSXStatus, status: "open" });
    }
  }, [getXLSXDataName]);

  async function getPPRXLSX() {
    const idToken = loginUser?.idToken;
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const nowTimeName = `${year}${month}${day}_${hours}${minutes}${seconds}`;
    const fileName = "推進実績_" + nowTimeName + ".xlsx";

    // search_params: getProductPartnerResultと同一serialization(buildPPRSearchParams+axios)
    const p = buildPPRSearchParams();
    const search_params = axios.getUri({ url: "", params: p }).replace(/^\?/, "");

    const bodys = {
      type: "get_xlsx_ppr",
      search_params,
      fileName,
    };
    const wsUrl = CONST.outputXLSXPPRWebSocketAPIURL + "?Authorization=" + idToken;
    const websocket = new WebSocket(wsUrl);
    let succeeded = false;
    let noProgressTimer: ReturnType<typeof setTimeout> | null = null;
    const clearNoProgressTimer = () => {
      if (noProgressTimer) { clearTimeout(noProgressTimer); noProgressTimer = null; }
    };
    const failXLSX = () => {
      if (succeeded) return;
      clearNoProgressTimer();
      setGetXLSXStatus({
        ...getXLSXStatus,
        status: "error",
        message: "出力に失敗しました。再度お試しください",
      });
      if (websocket.readyState === WebSocket.OPEN) websocket.close();
    };
    const armNoProgressTimer = () => {
      clearNoProgressTimer();
      noProgressTimer = setTimeout(failXLSX, 3 * 60 * 1000);
    };
    const onMessage = async (event_websocket: any) => {
      armNoProgressTimer();
      const decodedData = JSON.parse(event_websocket.data);
      if (decodedData.message === "success") {
        succeeded = true;
        clearNoProgressTimer();
        const dlUrl = decodedData.url;
        const link = document.createElement("a");
        link.href = dlUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(dlUrl);
        link.parentNode?.removeChild(link);
        setGetXLSXStatus({
          ...getXLSXStatus,
          status: "result",
          message: "推進実績出力完了",
        });
        websocket.close();
      } else if (
        decodedData.message !== "End_WebSocket_API" &&
        decodedData.message !== "Endpoint request timed out"
      ) {
        setGetXLSXStatus({
          ...getXLSXStatus,
          status: "getting",
          message: decodedData.message,
        });
      }
    };
    websocket.onmessage = onMessage;
    websocket.onopen = () => { armNoProgressTimer(); websocket.send(JSON.stringify(bodys)); };
    websocket.onclose = failXLSX;
  }

  const totalProductCount = data?.count_data ?? 0;
  const maxPageNumber = Math.max(
    1,
    Math.ceil(totalProductCount / displayNumber),
  );

  return (
    <Box
      data-testid="product-partner-result-page"
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <PageTabs group="analytics" />
      <Box
        sx={{
          display: "flex",
          gap: "var(--sp-2, 8px)",
          alignItems: "stretch",
          flex: 1,
          minHeight: 0,
        }}
      >
        {serchOpen && isSmartPhone ? null : (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TableTopBar
              serch={true}
              serchOpen={serchOpen}
              setSerchOpen={handleSetSerchOpen}
              addDataNames={[]}
              addDataName=""
              setAddDataName={() => {}}
              addCSVDataNames={[]}
              addCSVDataName=""
              setAddCSVDataName={() => {}}
              getXLSXDataNames={getXLSXDataNames}
              getXLSXDataName={getXLSXDataName}
              setGetXLSXDataName={setGetXLSXDataName}
              editDatasNames={[]}
              editDatasName=""
              setEditDatasName={() => {}}
              editIDs={[]}
              columnSettingOpen={false}
              openColumnSettingWindow={() => {}}
              onReload={() => fetchResult()}
              totalCount={totalProductCount}
            />
            <TableProductPartnerResult
              data={data}
              displayMode={displayMode}
              serchOpen={serchOpen}
              totalProductCount={totalProductCount}
              displayNumber={displayNumber}
              setDisplayNumber={setDisplayNumber}
              displayNumbers={DISPLAY_NUMBERS}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              maxPageNumber={maxPageNumber}
              fetchResult={fetchResult}
            />
          </Box>
        )}
        {serchOpen && (
          <SerchSideBar
            setSerchOpen={handleSetSerchOpen}
            tableColumn={productPartnerResultTableColumn}
            dataColumn={[]}
            firstYear={firstYear}
            thisYear={thisYear}
            serchColumn={serchColumnDef}
            setSerchString={setSerchString}
            companyNameList={saleCompanys.map((c) => c.sale_company_name)}
            companyGroupNameList={[]}
            highRankCompanyNameList={highRankCompanys.map(
              (c) => c.high_rank_company_name,
            )}
            categoryNameList={[]}
            productNameList={products.map((p) => p.product_name)}
            accountSaleNameList={[]}
            accountStaffNameList={[]}
            serchStringView={serchStringView}
            setSerchStringView={setSerchStringView}
            displayMode={displayMode}
            setDisplayMode={handleSetDisplayMode}
          />
        )}
      </Box>
      <GetXLSXPPRModal
        onModal={getXLSXStatus.status !== "none"}
        swCloseModal={() => {
          setGetXLSXStatus({ status: "none", message: "" });
          setGetXLSXDataName("");
        }}
        getXLSXStatus={getXLSXStatus}
        setGetXLSXStatus={setGetXLSXStatus}
        getXLSXFunc={getPPRXLSX}
        totalCount={totalProductCount}
      />
    </Box>
  );
}

export default ProductPartnerResult;
